import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { musician } from '@/mocks/musician';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import MediaPieChart from '@/components/feature/MediaPieChart';

const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Jackson Lacefield",
  "jobTitle": "Tenor & Worship Musician",
  "description": "Christian musician, tenor, and worship leader. Multi-instrumentalist in vocals, violin, piano, guitar, bass, and drums.",
  "url": "https://www.jacksonlacefield.com",
  "sameAs": [
    "https://www.youtube.com/@jacksonlacefield4977",
    "https://www.instagram.com/jacksonlacefieldmusic/"
  ],
  "alumniOf": {
    "@type": "CollegeOrUniversity",
    "name": "Baylor University"
  },
  "knowsAbout": ["Worship Music", "Classical Vocal Performance", "Choral Composition", "Violin", "Piano", "Guitar"],
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Dallas / Fort Worth",
    "addressRegion": "TX",
    "addressCountry": "US"
  }
};

export default function Home() {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const [activeTitle, setActiveTitle] = useState('');
  const parallaxRef = useRef<HTMLDivElement>(null);

  useScrollAnimation();

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrolled = window.scrollY;
        parallaxRef.current.style.transform = `translateY(${scrolled * 0.25}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openVideo = (embedUrl: string, title: string) => {
    setActiveVideo(embedUrl);
    setActiveTitle(title);
  };

  const closeVideo = () => {
    setActiveVideo(null);
    setActiveTitle('');
  };

  const featuredVideos = musician.mediaCategories[0].items.slice(0, 2);

  return (
    <>
      <title>Jackson Lacefield | Christian Musician, Tenor &amp; Multi-Instrumentalist</title>
      <meta name="description" content="Jackson Lacefield is a Christian musician and tenor at Baylor University. Multi-instrumentalist in vocals, violin, piano, guitar, bass, and drums. Leading worship and serving through music from Dallas / Ft. Worth." />
      <meta name="keywords" content="Jackson Lacefield, Christian musician, tenor, Baylor University, worship leader, multi-instrumentalist, Dallas, Fort Worth, church music, classical vocal performance" />
      <link rel="canonical" href="https://www.jacksonlacefield.com/" />
      <meta property="og:title" content="Jackson Lacefield | Christian Musician, Tenor &amp; Multi-Instrumentalist" />
      <meta property="og:description" content="Christian musician and tenor at Baylor University. Multi-instrumentalist leading worship through voice, guitar, piano, violin, drums, and bass." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.jacksonlacefield.com/" />
      <meta property="og:image" content="https://storage.readdy-site.link/project_files/d27b1ed1-d59b-43f0-8488-9d510448f5f8/45674621-1461-47a6-85af-49757092c6cf_compressed_A7404095_1_optimized_5000.webp" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Jackson Lacefield | Christian Musician, Tenor &amp; Multi-Instrumentalist" />
      <meta name="twitter:description" content="Christian musician and tenor at Baylor University. Multi-instrumentalist leading worship through voice, guitar, piano, violin, drums, and bass." />
      <meta name="twitter:image" content="https://storage.readdy-site.link/project_files/d27b1ed1-d59b-43f0-8488-9d510448f5f8/45674621-1461-47a6-85af-49757092c6cf_compressed_A7404095_1_optimized_5000.webp" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }} />
      <main className="min-h-screen bg-background-50">
      {/* ── Hero ── */}
      <section className="relative h-screen min-h-[600px] flex items-end justify-center overflow-hidden">
        <div ref={parallaxRef} className="absolute inset-0 w-full h-[120%] -top-[10%]">
          <img
            src={musician.heroImage}
            alt="Jackson Lacefield"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/30 to-foreground-950/80"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent opacity-50"></div>

        <div className="relative z-10 text-center px-4 md:px-6 max-w-4xl mx-auto pb-24 md:pb-32 w-full">
          <h1 className="font-heading text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-light text-white mb-4 animate-fade-in leading-tight">
            {musician.name}
          </h1>
          <p className="font-heading text-lg sm:text-xl md:text-3xl text-white/90 mb-6 animate-fade-in-delay">
            {musician.subtitle}
          </p>
          <div className="animate-fade-in-delay-2">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-accent-500 text-white px-6 sm:px-8 py-3 sm:py-3.5 rounded-full font-label text-sm tracking-wide hover:bg-accent-600 transition-all duration-500 cursor-pointer whitespace-nowrap shadow-lg shadow-accent-500/25"
            >
              <i className="ri-calendar-check-line"></i>
              Book Now
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 left-0 right-0 flex justify-center animate-fade-in-delay-3">
          <div className="flex flex-col items-center gap-2">
            <span className="font-label text-[10px] tracking-widest uppercase text-white/40">Scroll</span>
            <div className="w-px h-8 bg-white/25 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-3 bg-white/60 animate-scroll-bounce"></div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Video Player Overlay ── */}
      {activeVideo && (
        <div className="fixed inset-0 z-50 bg-foreground-950/90 flex items-center justify-center p-4 md:p-8" onClick={closeVideo}>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-heading text-lg text-background-50">{activeTitle}</p>
              <button onClick={closeVideo} className="w-8 h-8 flex items-center justify-center text-background-50 hover:text-background-50/80 transition-colors cursor-pointer" aria-label="Close video">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-foreground-900">
              <iframe
                src={activeVideo}
                title={activeTitle}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* ── Featured Media ── */}
      <section className="py-14 md:py-22 gradient-section-mid film-grain section-popout">
        <div className="section-container">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8 md:mb-12 scroll-animate">
            <div>
              <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-3">
                Featured Media
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-900">
                Watch &amp; Listen
              </h2>
            </div>
            <Link
              to="/music"
              className="inline-flex items-center gap-1 font-label text-sm text-accent-500 hover:text-accent-600 transition-colors duration-500 cursor-pointer whitespace-nowrap"
            >
              View All Media
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {featuredVideos.map((video, i) => (
              <div
                key={video.id}
                onClick={() => video.embedUrl && openVideo(video.embedUrl, video.title)}
                className="relative aspect-video rounded-2xl overflow-hidden cursor-pointer group bg-foreground-950 film-grain popout-card scroll-animate"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover object-top transition-all duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-background-50/90 flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:bg-accent-500">
                    <i className="ri-play-fill text-xl sm:text-2xl text-foreground-900 ml-0.5 group-hover:text-background-50 transition-colors"></i>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <span className="inline-block bg-accent-500 text-white font-label text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full mb-2">
                    {video.context}
                  </span>
                  <p className="font-heading text-lg sm:text-xl md:text-2xl text-white font-medium drop-shadow-md">{video.title}</p>
                  <p className="font-body text-xs md:text-sm text-white/90 mt-1 hidden sm:block drop-shadow-sm">{video.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pie Chart: A Well-Rounded Performer ── */}
      <section className="py-14 md:py-24 gradient-section-orange-deeper film-grain section-popout-orange">
        <div className="section-container">
          <div className="mb-8 md:mb-12 text-center scroll-animate">
            <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-4">
              Explore
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-3">
              A Well-Rounded Performer
            </h2>
            <p className="font-body text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              From worship leading to classical violin — I bring versatility and passion to every stage.
            </p>
          </div>

          <div className="scroll-animate">
            <MediaPieChart />
          </div>
        </div>
      </section>

      {/* ── About Teaser ── */}
      <section className="py-14 md:py-22 gradient-section-mid film-grain section-popout">
        <div className="section-container max-w-3xl mx-auto text-center scroll-animate">
          <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-4">
            About
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-900 mb-5 leading-tight">
            {musician.about.headline}
          </h2>
          <p className="font-body text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
            {musician.about.bio}
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 font-label text-sm text-accent-500 hover:text-accent-600 transition-colors duration-500 cursor-pointer whitespace-nowrap"
          >
            Read Full Story
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </main>
    </>
  );
}